import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const REAL_SRC = "/audio/danielles-song.mp3";
const PLACEHOLDER_SRC = "/audio/placeholder.wav";

export default function AudioPlayer() {
    const audioRef = useRef(null);
    const [src, setSrc] = useState(REAL_SRC);
    const [isPlaceholder, setIsPlaceholder] = useState(false);
    const [ready, setReady] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [current, setCurrent] = useState(0);

    // Fallback: probe for the real MP3. Reject HTML responses (dev-server SPA
    // fallback) and only accept audio/* or octet-stream content-types.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(REAL_SRC, {
                    method: "GET",
                    headers: { Range: "bytes=0-1" },
                });
                if (cancelled) return;
                const ct = (r.headers.get("content-type") || "").toLowerCase();
                const isAudioContentType =
                    ct.startsWith("audio/") || ct === "application/octet-stream";
                if (!r.ok || ct.includes("html") || !isAudioContentType) {
                    setSrc(PLACEHOLDER_SRC);
                    setIsPlaceholder(true);
                }
            } catch {
                if (!cancelled) {
                    setSrc(PLACEHOLDER_SRC);
                    setIsPlaceholder(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onLoaded = () => {
            setReady(true);
            setDuration(a.duration || 0);
        };
        const onTime = () => {
            setCurrent(a.currentTime);
            if (a.duration) setProgress((a.currentTime / a.duration) * 100);
        };
        const onEnd = () => {
            setPlaying(false);
            setProgress(0);
            setCurrent(0);
        };
        const onError = () => setReady(false);
        a.addEventListener("loadedmetadata", onLoaded);
        a.addEventListener("timeupdate", onTime);
        a.addEventListener("ended", onEnd);
        a.addEventListener("error", onError);
        return () => {
            a.removeEventListener("loadedmetadata", onLoaded);
            a.removeEventListener("timeupdate", onTime);
            a.removeEventListener("ended", onEnd);
            a.removeEventListener("error", onError);
        };
    }, [src]);

    const toggle = async () => {
        const a = audioRef.current;
        if (!a || !ready) return;
        try {
            if (a.paused) {
                await a.play();
                setPlaying(true);
            } else {
                a.pause();
                setPlaying(false);
            }
        } catch {
            /* browser blocked autoplay or file missing */
        }
    };

    const seek = (e) => {
        const a = audioRef.current;
        if (!a || !ready || !a.duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        a.currentTime = pct * a.duration;
    };

    const fmt = (s) => {
        if (!s || Number.isNaN(s)) return "0:00";
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60).toString().padStart(2, "0");
        return `${m}:${sec}`;
    };

    return (
        <section
            data-testid="audio-section"
            className="relative w-full bg-[color:var(--paper)] py-24 sm:py-32 border-t border-neutral-300"
            id="song"
        >
            <div className="max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 1 }}
                    className="grid grid-cols-12 gap-6 items-end"
                >
                    <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                        Chapter VI — The Song
                    </div>
                    <div className="col-span-12 sm:col-span-9">
                        <h2 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-neutral-950">
                            <em className="not-italic italic font-light">I Will Never Surrender.</em>
                        </h2>
                        <p className="mt-4 font-sans text-[10px] sm:text-xs uppercase tracking-[0.35em] text-neutral-500">
                            Written by Rebecca Charles · For her daughter, Danielle.
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 1, delay: 0.15 }}
                    className="mt-14 sm:mt-20 border-y border-neutral-300 py-10 sm:py-14 grid grid-cols-12 gap-6 items-center"
                >
                    <div className="col-span-3 sm:col-span-2">
                        <button
                            onClick={toggle}
                            disabled={!ready}
                            data-testid="audio-play-button"
                            aria-label={playing ? "Pause Danielle's song" : "Play Danielle's song"}
                            className="group relative w-20 h-20 sm:w-24 sm:h-24 border border-neutral-900 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-700 hover:bg-neutral-900"
                        >
                            <span className="absolute inset-0 origin-bottom scale-y-0 group-hover:scale-y-100 group-disabled:scale-y-0 bg-neutral-900 transition-transform duration-700 ease-[cubic-bezier(0.77,0,0.175,1)]" />
                            <span className="relative text-neutral-900 group-hover:text-neutral-50 group-disabled:text-neutral-900 transition-colors duration-500">
                                {playing ? (
                                    <span className="flex gap-1.5">
                                        <span className="block w-[3px] h-6 bg-current" />
                                        <span className="block w-[3px] h-6 bg-current" />
                                    </span>
                                ) : (
                                    <svg width="16" height="20" viewBox="0 0 16 20" fill="currentColor" className="ml-0.5">
                                        <path d="M0 0 L16 10 L0 20 Z" />
                                    </svg>
                                )}
                            </span>
                        </button>
                    </div>

                    <div className="col-span-9 sm:col-span-10">
                        <div className="flex items-baseline justify-between mb-4">
                            <div>
                                <div className="font-serif italic text-2xl sm:text-3xl leading-none text-neutral-950">
                                    I Will Never Surrender
                                </div>
                                <div className="mt-1 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                    {isPlaceholder
                                        ? "Placeholder score · awaiting Danielle's Song"
                                        : "Written by Rebecca Charles"}
                                </div>
                            </div>
                            <div className="font-sans tabular text-xs sm:text-sm text-neutral-600">
                                {fmt(current)} <span className="text-neutral-400">/</span>{" "}
                                {ready ? fmt(duration) : "—:—"}
                            </div>
                        </div>

                        <div
                            onClick={seek}
                            className="relative h-px bg-neutral-300 cursor-pointer group"
                            data-testid="audio-progress-track"
                            role="slider"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={progress}
                            tabIndex={0}
                        >
                            <div className="absolute inset-y-0 left-0 -my-2 group-hover:-my-3 transition-all" />
                            <div className="absolute left-0 top-0 bottom-0 bg-neutral-900" style={{ width: `${progress}%` }} />
                            <span
                                className="absolute -top-1 w-2 h-2 bg-neutral-900 -ml-1 transition-transform duration-300 group-hover:scale-125"
                                style={{ left: `${progress}%` }}
                            />
                        </div>

                        {isPlaceholder && (
                            <div className="mt-4 font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                A soft placeholder plays until Danielle&apos;s Song is uploaded to /audio/danielles-song.mp3
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>

            <audio ref={audioRef} src={src} preload="metadata" data-testid="audio-element" />
        </section>
    );
}
